import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Logo from './Logo';
import Navigation from './Navigation';
import { Eye, EyeOff } from 'lucide-react';
import { API_BASE } from '@/config/api';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if signup parameter is present
  useEffect(() => {
    if (searchParams.get('signup') === 'true') {
      navigate('/register');
    }
  }, [searchParams, navigate]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });

  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    setError('');

    try {
      const apiUrl = API_BASE;
      const oauthUrl = `${apiUrl}/auth/google/login`;
      
      const popupWindow = window.open(
        oauthUrl,
        'oauth_popup',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popupWindow) {
        window.location.href = oauthUrl;
        return;
      }

      const handleMessage = async (event: MessageEvent) => {
        const backendOrigin = API_BASE.replace(/\/+$/, '').replace(/\r?\n/g, '');
        const allowedOrigins = [window.location.origin, backendOrigin];

        if (!event.data || typeof event.data !== 'object' || !('type' in event.data)) {
          return;
        }

        const messageType = event.data.type;
        const isOAuthMessage = messageType === 'OAUTH_SUCCESS' || messageType === 'OAUTH_ERROR';
        if (!allowedOrigins.includes(event.origin) && !isOAuthMessage) {
          return;
        }

        if (messageType === 'OAUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          setSocialLoading(null);

          const { access_token, refresh_token, user, redirect_path } = event.data;
          localStorage.setItem('authToken', access_token);
          if (refresh_token) {
            localStorage.setItem('refreshToken', refresh_token);
          }

          const userData = {
            id: user.id.toString(),
            email: user.email,
            name: user.full_name,
            role: user.role,
            tier: user.tier || 'free',
            timezone: user.timezone || 'Asia/Kolkata',
            phone: user.phone_number || undefined,
            emailVerifiedAt: user.email_verified_at || null,
            phoneVerifiedAt: user.phone_verified_at || null,
            preferredVerificationMethod: user.preferred_verification_method || null,
          };
          localStorage.setItem('user', JSON.stringify(userData));

          if (user.role === 'pending') {
            localStorage.setItem('oauthUserData', JSON.stringify({
              email: user.email,
              name: user.full_name,
              fromOAuth: true
            }));
            navigate('/register?mode=signup&oauth=true');
            return;
          }

          const destination = redirect_path || 
            (user.role === 'customer' ? '/customer/dashboard' :
             user.role === 'tailor' ? '/business/dashboard' :
             user.role === 'admin' ? '/admin/dashboard' : '/customer/dashboard');
          navigate(destination);
          window.location.reload();
        } else if (messageType === 'OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          setSocialLoading(null);
          setError(event.data.error || 'Google login failed.');
        }
      };

      window.addEventListener('message', handleMessage);

    } catch (_error) {
      setError('Google login failed. Please try again.');
      setSocialLoading(null);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSignUp && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isSignUp) {
        // Registration
        const apiUrl = API_BASE;
        const response = await fetch(`${apiUrl}/api/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            role: 'customer' // Default role
          }),
        });

        if (response.ok) {
          // Auto-login after successful registration
          const success = await login(formData.email, formData.password);
          if (success) {
            navigate('/customer/dashboard');
          }
        } else {
          const data = await response.json();
          setError(data.detail || 'Registration failed. Please try again.');
        }
      } else {
        // Login
        const success = await login(formData.email, formData.password);
        if (success) {
          navigate('/customer/dashboard');
        } else {
          setError('Invalid credentials. Please try again.');
        }
      }
    } catch (_error) {
      setError(isSignUp ? 'Registration failed. Please try again.' : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue/10 via-brand-white to-brand-yellow/10">
      {/* Header */}
      <Navigation 
        showBackButton={true}
        showHomeButton={true}
        backTo="/"
        title="Authentication"
      />
      
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 flex-1">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Logo size="3xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-gray-600">
              {isSignUp ? 'Sign up to get started with MasterJi' : 'Sign in to your MasterJi account'}
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-brand-white rounded-lg shadow-lg p-8">
            {/* Google OAuth Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={socialLoading !== null || isLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-brand-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:opacity-50 disabled:cursor-not-allowed mb-6"
            >
              {socialLoading === 'google' ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-blue"></div>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {isSignUp ? 'Sign up with Google' : 'Sign in with Google'}
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-brand-white text-gray-500">or</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              {isSignUp && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required={isSignUp}
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {isSignUp && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required={isSignUp}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Forgot Password Link - Only show on login mode */}
              {!isSignUp && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-brand-blue hover:text-brand-blue/80 font-medium"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || socialLoading !== null}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-brand-white bg-brand-blue hover:bg-brand-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-white"></div>
                ) : (
                  isSignUp ? 'Create Account' : 'Sign In'
                )}
              </button>
            </form>

            {/* Toggle Sign Up/Sign In */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {isSignUp ? "Already have an account?" : "Don't have an account?"}{' '}
                <button
                  onClick={() => {
                    if (isSignUp) {
                      // Navigate to signin
                      navigate('/auth?mode=signin');
                    } else {
                      // Navigate to signup to ensure proper flow with email verification
                      navigate('/auth?mode=signup');
                    }
                  }}
                  className="text-brand-blue hover:text-brand-blue/80 font-medium"
                >
                  {isSignUp ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 
